import type { ReactElement } from "react";
import { cardStyles } from "../core/Card.styles.js";
import { CardLink } from "../core/CardLink.js";
import { OptionalImage } from "../foundations/OptionalImage.js";
import { useImageFallback } from "../foundations/useImageFallback.js";
import { Amount } from "./Amount.js";
import { ArtPlaceholder } from "./ArtPlaceholder.js";

export interface ProductCardProps {
  name: string;
  /** A product photo cut out on transparent: a graded slab, a sealed box. */
  image: string;
  /** Integer cents. */
  price: number;
  /** One line under the name: set, grade, language. */
  meta?: string;
  /** A short tag in the well's corner: PSA 10, SEALED. */
  tag?: string;
  /** Links the card to the product (a stretched link on its name). Without it the card is static. */
  href?: string;
  /** The app's router link, e.g. `<Link to="/exchange/umbreon" />`. */
  render?: ReactElement;
  className?: string;
}

/** One real, physical product (a graded card, a sealed box) with its price. */
export function ProductCard({
  name,
  image,
  price,
  meta,
  tag,
  href,
  render,
  className,
}: ProductCardProps) {
  const photo = useImageFallback(image);
  const linked = Boolean(href || render);
  return (
    <div
      className={cardStyles({
        radius: "2xl",
        action: linked ? "link" : "none",
        hover: linked ? "raise" : "none",
        className: ["group relative flex flex-col gap-3 p-2 pb-4", className],
      })}
    >
      <span className="relative block aspect-[4/5] overflow-hidden rounded-xl bg-bg-inset">
        <OptionalImage
          src={image}
          alt=""
          aria-hidden
          className="absolute inset-0 size-full scale-110 object-cover opacity-30 blur-2xl"
        />
        <span className="absolute inset-0 flex items-center justify-center p-[14%]">
          {photo.failed ? (
            <ArtPlaceholder kind="item" />
          ) : (
            <img
              src={image}
              alt=""
              loading="lazy"
              onError={photo.onError}
              className="max-h-full max-w-full object-contain drop-shadow-[0_16px_24px_color-mix(in_oklab,black_50%,transparent)] transition-transform duration-base ease-standard group-hover:-translate-y-1 motion-reduce:transition-none"
            />
          )}
        </span>
        {tag && (
          <span className="absolute top-3 left-3 font-mono text-mono-meta text-text-secondary uppercase">
            {tag}
          </span>
        )}
      </span>
      <span className="flex flex-col gap-1 px-2">
        <span className="truncate font-body text-heading-h3 text-text-primary">
          <CardLink href={href} render={render}>
            {name}
          </CardLink>
        </span>
        {meta && <span className="font-body text-body-b3 text-text-tertiary">{meta}</span>}
        <Amount cents={price} className="pt-1 text-text-primary" />
      </span>
    </div>
  );
}
